import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { LocalStorageService, SessionStorageService } from 'angular-web-storage';
import { catchError, of } from 'rxjs';
import { environment } from 'src/environment/environment';
import { AuditEvent, AuditEventData } from '../_types/audit-events';
import { AuditContextService } from './audit-context.service';

/// Registro de actividad del usuario.
///
/// Es deliberadamente a prueba de fallos: `track()` encola y vuelve de inmediato, y el envío se hace con
/// un HttpClient construido sobre HttpBackend, que NO pasa por los interceptores de la aplicación. Eso es
/// imprescindible porque HttpErrorInterceptor muestra un toast por cada error y, ante un 401, cierra la
/// sesión: un fallo registrando actividad echaría al usuario de la aplicación. Al no pasar por
/// HttpConfigInterceptor tampoco cuenta para el loader global, pero hay que poner el token a mano.
@Injectable({
    providedIn: 'root'
})
export class AuditService {

    private static readonly SESSION_STORAGE_KEY = 'auditSessionId';

    /// Cada cuánto se vacía la cola.
    private static readonly FLUSH_INTERVAL_MS = 10 * 1000;

    /// Tope de la cola. Si el backend está caído, se descartan los eventos más antiguos en lugar de
    /// crecer sin límite en memoria.
    private static readonly MAX_QUEUE_SIZE = 200;

    /// Intentos de envío de un evento antes de descartarlo.
    private static readonly MAX_ATTEMPTS = 2;

    private readonly http: HttpClient;
    private readonly endpoint = environment.api_gw + 'auth/userHistories/events';

    private queue: AuditEvent[] = [];
    private attempts: number = 0;
    private sending: boolean = false;

    constructor(
        private storage: LocalStorageService,
        private sessionStorage: SessionStorageService,
        private auditContextService: AuditContextService
    ) {
        //HttpClient propio sobre HttpBackend: se salta los tres interceptores de la aplicación
        this.http = new HttpClient(inject(HttpBackend));

        setInterval(() => this.flush(), AuditService.FLUSH_INTERVAL_MS);

        //si el usuario cierra o esconde la pestaña, enviamos lo que quede pendiente
        window.addEventListener('beforeunload', () => this.flush());
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') this.flush();
        });
    }

    /// Registra una acción del usuario. Nunca lanza excepciones ni bloquea al llamante.
    track(eventCode: string, data: AuditEventData | null = null) {
        try {
            if (!eventCode) return;

            //sin usuario no hay nada que auditar: el backend descartaría el evento igualmente
            if (this.storage.get('user') == null) return;

            const metadata = this.buildMetadata(data);

            const event: AuditEvent = {
                eventCode: eventCode,
                date: new Date().toISOString(),
                caseId: data?.caseId ?? this.auditContextService.getCaseId(),
                entityType: data?.entityType ?? null,
                entityId: data?.entityId ?? null,
                fieldName: data?.fieldName ?? null,
                previousValue: this.toText(data?.previousValue),
                newValue: this.toText(data?.newValue),
                reason: data?.reason ?? null,
                metadata: metadata,
                information: data?.information ?? null,
                sessionId: this.getSessionId(),
                correlationId: this.newId()
            };

            this.queue.push(event);

            //si la cola desborda, se pierden los eventos más antiguos
            if (this.queue.length > AuditService.MAX_QUEUE_SIZE) {
                this.queue.splice(0, this.queue.length - AuditService.MAX_QUEUE_SIZE);
            }
        } catch (error) {
            //la trazabilidad nunca puede romper el flujo del usuario
            console.warn('No se ha podido registrar el evento de actividad', eventCode, error);
        }
    }

    /// Arranca una sesión de auditoría nueva. Se llama al iniciar sesión.
    startSession() {
        try {
            this.sessionStorage.set(AuditService.SESSION_STORAGE_KEY, this.newId());
        } catch (error) {
            console.warn('No se ha podido iniciar la sesión de auditoría', error);
        }
    }

    /// Envía la cola. Se llama de forma periódica, al esconder la pestaña y al cerrarla.
    flush() {
        try {
            if (this.sending || this.queue.length === 0) return;

            const token = this.storage.get('token');
            if (token == null) {
                //sin token no se puede enviar; se descarta para no acumular indefinidamente
                this.queue = [];
                this.attempts = 0;
                return;
            }

            const batch = this.queue;
            this.queue = [];
            this.sending = true;

            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token
            });

            this.http.post(this.endpoint, batch, { headers: headers })
                .pipe(catchError((error) => {
                    //ni toast ni logout: solo consola. Reintentamos una vez por si fue un corte puntual
                    console.warn('No se ha podido enviar el registro de actividad', error?.status);
                    this.attempts++;
                    if (this.attempts < AuditService.MAX_ATTEMPTS) {
                        this.queue = batch.concat(this.queue);
                        //el lote devuelto puede desbordar la cola: descartamos los eventos más antiguos
                        if (this.queue.length > AuditService.MAX_QUEUE_SIZE) {
                            this.queue.splice(0, this.queue.length - AuditService.MAX_QUEUE_SIZE);
                        }
                    } else {
                        this.attempts = 0;
                    }
                    return of(null);
                }))
                .subscribe({
                    next: () => {
                        this.attempts = 0;
                        this.sending = false;
                    },
                    error: () => {
                        //catchError ya lo ha absorbido, pero nos aseguramos de liberar el envío
                        this.sending = false;
                    },
                    complete: () => {
                        this.sending = false;
                    }
                });
        } catch (error) {
            this.sending = false;
            console.warn('No se ha podido enviar el registro de actividad', error);
        }
    }

    private buildMetadata(data: AuditEventData | null): { [key: string]: string } | null {
        const metadata: { [key: string]: string } = {};

        //el submódulo activo del expediente se guarda como contexto del evento
        const submodule = this.auditContextService.getSubmodule();
        if (submodule) metadata['submodule'] = submodule;

        if (data?.metadata) {
            for (const key of Object.keys(data.metadata)) {
                const value = this.toText(data.metadata[key]);
                if (value != null) metadata[key] = value;
            }
        }

        return Object.keys(metadata).length > 0 ? metadata : null;
    }

    private getSessionId(): string {
        let sessionId = this.sessionStorage.get(AuditService.SESSION_STORAGE_KEY);
        if (!sessionId) {
            sessionId = this.newId();
            this.sessionStorage.set(AuditService.SESSION_STORAGE_KEY, sessionId);
        }
        return sessionId;
    }

    /// Convierte cualquier valor a texto para poder guardarlo como valor previo o nuevo.
    private toText(value: any): string | null {
        if (value === null || value === undefined || value === '') return null;
        if (value instanceof Date) return value.toISOString();
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch {
                return null;
            }
        }
        return String(value);
    }

    private newId(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();

        //navegadores sin randomUUID o contextos no seguros
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

}
